import dayjs from "dayjs";
import { renderToString } from "react-dom/server";

import { fullMonthAndYear, LineGraph, shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip, LegendBullet } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import { NoData } from "pro-features/components/NoData/src/NoData";

import type { TrafficAndEngagementChartProps } from "../types";
import { getValueFormatter } from "../utils/utils";

type RowData = {
    country: {
        label: string;
        color: string;
    };
    value: string;
};

const TOOLTIP_COLUMN_DEFINITION: ColumnDefinition<RowData>[] = [
    {
        title: "",
        key: "country",
        selector: "country" as const,
        align: "left",
        size: "120px",
        render: ({ country }) => {
            return (
                <ChartTooltip.LabelCell
                    label={country.label}
                    backgroundColor={country.color}
                />
            );
        },
    },
    {
        title: "",
        key: "value",
        selector: "value" as const,
        align: "right",
        size: "80px",
        render: ({ value }) => (
            <ChartTooltip.ValueCell
                data-automation="geography-country-tooltip-value"
                value={value}
            />
        ),
    },
];

export const IndustryAnalysisGeographyTrafficAndEngagementChart = ({
    data,
    legends,
    isLoading = false,
    selectedMetric,
}: TrafficAndEngagementChartProps) => {
    if (isLoading) {
        return <GraphLoader width="100%" />;
    }

    if (!data || data.length === 0) {
        return <NoData />;
    }

    const valueFormatter = getValueFormatter(selectedMetric);

    return (
        <Flex
            flexDirection="column"
            gap="24px"
        >
            <Flex>
                {legends.map((legend) => (
                    <LegendBullet
                        key={legend.countryId}
                        text={legend.name}
                        labelColor={legend.color}
                    />
                ))}
            </Flex>
            <LineGraph
                withMarkers
                data={data}
                shouldPresentLegend={false}
                graphConfig={{
                    chart: {
                        height: 280,
                    },
                    xAxis: {
                        type: "datetime",
                        labels: {
                            formatter: function ({ value }) {
                                return dayjs.utc(value).format(shortMonthAndShortYear);
                            },
                        },
                    },
                    yAxis: {
                        labels: {
                            formatter: function ({ value }) {
                                return valueFormatter(Number(value));
                            },
                        },
                    },
                    tooltip: {
                        shared: true,
                        outside: true,
                        useHTML: true,
                        formatter: function () {
                            const title = dayjs.utc(this.x).format(fullMonthAndYear);
                            const rowsData: RowData[] = this.points.map((point) => ({
                                country: {
                                    label: point.series.name,
                                    color: point.color as string,
                                },
                                value: valueFormatter(point.y),
                            }));

                            return renderToString(
                                <ChartTooltip
                                    withoutColumnTitles
                                    title={title}
                                    columnDefinition={TOOLTIP_COLUMN_DEFINITION}
                                    data={rowsData}
                                />,
                            );
                        },
                    },
                }}
            />
        </Flex>
    );
};
